import React, { useState } from 'react';
import { translations } from '../translations';
import { FileText } from 'lucide-react';

const ROICalculator = ({ language = 'en' }) => {
  const t = translations[language].roi;
  const [fleetSize, setFleetSize] = useState(50);
  const [mileage, setMileage] = useState(5000);
  const [fuelCost, setFuelCost] = useState(1.80);
  const [maintenanceCost, setMaintenanceCost] = useState(500);

  const calculateSavings = () => {
    const fuelSavings = (fleetSize * mileage * 0.15 * fuelCost) / 100;
    const maintenanceSavings = fleetSize * maintenanceCost * 0.25;
    const efficiencySavings = (fuelSavings + maintenanceSavings) * 0.3;
    
    return {
      fuel: Math.round(fuelSavings * 12),
      maintenance: Math.round(maintenanceSavings * 12),
      efficiency: Math.round(efficiencySavings * 12),
      total: Math.round((fuelSavings + maintenanceSavings + efficiencySavings) * 12)
    };
  };

  const savings = calculateSavings();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{t.title}</h1>
          <p className="text-xl text-gray-600 mb-8">{t.subtitle}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="space-y-8">
            {/* Fleet Size */}
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">{t.form.fleetSize}</label>
                <span className="text-sm font-medium text-[#3498DB]">{fleetSize} vehicles</span>
              </div>
              <input
                type="range"
                min="10"
                max="500"
                value={fleetSize}
                onChange={(e) => setFleetSize(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#3498DB]"
              />
            </div>

            {/* Average Monthly Mileage per Vehicle */}
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">{t.form.mileage}</label>
                <span className="text-sm font-medium text-[#3498DB]">{mileage} km</span>
              </div>
              <input
                type="range"
                min="1000"
                max="20000"
                step="100"
                value={mileage}
                onChange={(e) => setMileage(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#3498DB]"
              />
            </div>

            {/* Fuel Cost */}
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">{t.form.fuelCost}</label>
                <span className="text-sm font-medium text-[#3498DB]">€{fuelCost.toFixed(2)} per liter</span>
              </div>
              <input
                type="range"
                min="1.00"
                max="3.00"
                step="0.01"
                value={fuelCost}
                onChange={(e) => setFuelCost(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#3498DB]"
              />
            </div>

            {/* Monthly Maintenance Cost per Vehicle */}
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">{t.form.maintenanceCost}</label>
                <span className="text-sm font-medium text-[#3498DB]">€{maintenanceCost}</span>
              </div>
              <input
                type="range"
                min="200"
                max="2000"
                step="50"
                value={maintenanceCost}
                onChange={(e) => setMaintenanceCost(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#3498DB]"
              />
            </div>
          </div>

          {/* Results Section */}
          <div className="mt-12 bg-gray-50 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">{t.results.title}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-green-50 rounded-lg">
                    <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t.results.fuelSavings.label}</p>
                    <p className="text-2xl font-bold text-green-600">€{savings.fuel.toLocaleString()}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t.results.maintenanceSavings.label}</p>
                    <p className="text-2xl font-bold text-blue-600">€{savings.maintenance.toLocaleString()}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-purple-50 rounded-lg">
                    <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t.results.efficiencySavings.label}</p>
                    <p className="text-2xl font-bold text-purple-600">€{savings.efficiency.toLocaleString()}</p>
                  </div>
                </div>
              </div>
              <div className="bg-[#3498DB] p-4 rounded-lg text-white">
                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-white/80">{t.results.totalSavings.label}</p>
                    <p className="text-2xl font-bold">€{savings.total.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="mt-8 flex justify-center">
            <button className="flex items-center space-x-2 bg-[#3498DB] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#2980B9] transition-colors duration-300">
              <FileText className="h-5 w-5" />
              <span>{t.cta.calculate}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ROICalculator; 